import type React from "react";
import { useCallback } from "react";

interface CitationGroupChipOptions {
  /** More than one citation behind the chip. When false the hook adds nothing. */
  isGroup: boolean;
  /** The tooltip is visible and showing this chip's citations. */
  isOpen: boolean;
  isPinned: boolean;
  /** Show this chip's citations; `pinned` keeps the tooltip open until closed. */
  open: (pinned: boolean) => void;
  close: () => void;
  page: (delta: number) => void;
}

/**
 * Click, tap and keyboard handling for a chip that merges several citations.
 * Click/tap/Enter/Space pins the tooltip open (and closes it when pinned),
 * arrow keys page, Escape closes. Hover keeps working through the chip's own
 * mouse handlers. A single citation gets no extra props, so its chip renders
 * and behaves exactly as before.
 */
export const useCitationGroupChip = ({
  isGroup,
  isOpen,
  isPinned,
  open,
  close,
  page,
}: CitationGroupChipOptions): {
  onClick?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  "aria-expanded"?: boolean;
} => {
  const toggle = useCallback(() => {
    if (isOpen && isPinned) {
      close();
    } else {
      open(true);
    }
  }, [isOpen, isPinned, open, close]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          toggle();
          break;
        case "ArrowRight":
        case "ArrowDown":
          if (isOpen) {
            event.preventDefault();
            page(1);
          }
          break;
        case "ArrowLeft":
        case "ArrowUp":
          if (isOpen) {
            event.preventDefault();
            page(-1);
          }
          break;
        case "Escape":
          if (isOpen) close();
          break;
      }
    },
    [isOpen, toggle, page, close]
  );

  if (!isGroup) return {};

  return { onClick: toggle, onKeyDown, "aria-expanded": isOpen && isPinned };
};
